import { useQuery } from "@tanstack/react-query";
import { useTabDraft } from "@/components/ui/box-draft";
import type { Blocker, Part } from "@/components/ui/box.schema";
import { facultyApi } from "@/lib/api/faculty";
import { personChoice } from "@/lib/person-name";
import { carryFacultyChoices, enrollmentExportTemplateCsv, facultyChoiceOf, facultyNeedsChoice, facultySourceName, readMappedRecords, readRecordsSource, recordsTemplateCsv, RECORDS_FIELDS, summariseRecords, withFacultyChoice } from "@/lib/historical-records-source";
import { prefillColumns, type ColumnMap, type SourceTable } from "@/lib/source-columns";
import { importsText as text, importsWords as words } from "./imports-copy";
import { columnsText, columnsWords, RECORDS_DERIVATION, RECORDS_FIELD_NOTES } from "./source-columns-copy";
import { columnMapOf, columnsBlockers, columnsPart, fieldLines } from "./source-columns-part";
import { SourceFormat, type FormatLine, type FormatShape } from "./SourceFormat";
import { useImportTask, type ImportSource } from "./useImportTask";

const BOX = "history-import-records";
const INSTRUCTORS = `${BOX}-instructors`;
const COLUMNS = `${BOX}-columns`;

const FORMAT: FormatLine[] = [
  { column: "record", readsWhen: "Every row", note: "term, faculty, course, student, enrollment or outcome" },
  { column: "key", readsWhen: "Every row", note: "Your own stable key for the row. Other rows refer to it by this key, and a rerun of the same key matches instead of creating again." },
  { column: "name", readsWhen: "term, faculty, student", note: "The term's name, the instructor's name as the source writes it, or the learner's legal name" },
  { column: "start_date, end_date", readsWhen: "term", note: "YYYY-MM-DD, optional. The term is recorded as closed." },
  { column: "term_key, code, title, credits", readsWhen: "course", note: "The section's term row, institutional course code, title and credits. Credits are required." },
  { column: "primary_faculty_key, co_instructor_faculty_keys", readsWhen: "course", note: "Instructor row keys; co-instructors separated by ;" },
  { column: "student_id", readsWhen: "student", note: "The source's SID, when it has one. Without it you choose the learner's record in the review." },
  { column: "email", readsWhen: "student", note: "Contact evidence only. It never chooses or matches a person." },
  { column: "status", readsWhen: "course, student, enrollment", note: "course: archived or draft · student: completed, active, leave_of_absence or withdrawn · enrollment: complete, enrolled, withdrawn or incomplete" },
  { column: "student_key, course_key", readsWhen: "enrollment", note: "The student row's and section row's keys" },
  { column: "enrollment_key, result_letter, result_score", readsWhen: "outcome", note: "A certified result for the enrollment row: its letter and optional score 0-100" },
  { column: "reason", readsWhen: "Never", note: "A refused-rows export writes each refusal here. It is ignored, so the export reads back as saved." },
];

const SHAPES: FormatShape[] = [
  { title: columnsWords.templateShape, note: words.templateNote, readsWhen: words.readsWhen, lines: FORMAT,
    template: { name: "closed-term-records-template.csv", verb: words.downloadTemplate, text: recordsTemplateCsv } },
  { title: columnsWords.exportShape, note: columnsWords.exportNote, readsWhen: columnsWords.needed, lines: fieldLines(RECORDS_FIELDS, RECORDS_FIELD_NOTES),
    template: { name: "closed-term-enrollments-example.csv", verb: columnsWords.downloadExample, text: enrollmentExportTemplateCsv } },
];

/** An export's table, kept in this tab with the source it was read into, so its columns can be chosen again. */
interface KeptTable { digest: string; table: SourceTable }

/** A closed-term records file: IMAM's template, the registrar's keys, every record an explicit choice. */
export function useRecordsFileBox(rank: number | null = null) {
  const faculty = useQuery({ queryKey: ["import-faculty-records"], queryFn: () => facultyApi.list() });
  const kept = useTabDraft<KeptTable | null>({ box: COLUMNS, record: "table", version: null, initial: null });
  const tableOf = (source: ImportSource | null) => source && kept.value?.digest === source.digest ? kept.value.table : null;
  const undecided = (source: ImportSource | null) => source?.rows.filter((r) => facultyNeedsChoice(r) && !facultyChoiceOf(r)) ?? [];
  const sourceBlockers = (source: ImportSource | null): Blocker[] => {
    const n = undecided(source).length;
    return [...columnsBlockers(COLUMNS, source, RECORDS_FIELDS), ...(n ? [{ kind: "missing" as const, part: INSTRUCTORS, sentence: text.instructorsUndecided(n) }] : [])];
  };
  const sourceParts = (source: ImportSource | null, setSource: (s: ImportSource | null) => void): Part[] => {
    const table = tableOf(source);
    const remap = (map: ColumnMap) => {
      if (!source || !table) return;
      const reading = readMappedRecords(table, map);
      setSource({ ...source, rows: carryFacultyChoices(source.rows, reading.rows), problems: reading.problems, review: map });
    };
    const columns = columnsPart({ id: COLUMNS, source, table, fields: RECORDS_FIELDS, derivation: RECORDS_DERIVATION, onMap: remap });
    const rows = source?.rows.filter(facultyNeedsChoice) ?? [];
    const waiting = undecided(source).length > 0;
    const choose = (key: string, value: string) => {
      if (!source) return;
      const choice = value === "create" ? { action: "create" as const } : value ? { action: "link" as const, faculty_profile_id: value } : null;
      setSource({ ...source, rows: source.rows.map((r) => facultyNeedsChoice(r) && r.import_key === key ? withFacultyChoice(r, choice) : r) });
    };
    return [columns, { id: INSTRUCTORS, label: words.instructors, owner: { system: "imam" }, fold: "choice", draft: "browser",
      state: { class: waiting ? "waiting" : "draft", word: waiting ? words.instructorsNeeded : words.instructorsChosen },
      render: () => !source ? <p>{words.readFirst}</p> : !rows.length ? <p>{words.noInstructorRows}</p> : <div className="grid gap-4 text-ui">
        <p>{words.instructorsIntro}</p>
        {faculty.isPending && <p role="status">{words.readingFaculty}</p>}
        {faculty.isError && <p role="alert">{words.facultyCouldNotBeRead} <button type="button" onClick={() => void faculty.refetch()}>{words.tryAgain}</button></p>}
        {rows.map((r) => {
          const name = facultySourceName(r);
          const choice = facultyChoiceOf(r);
          const value = choice?.action === "create" ? "create" : choice?.action === "link" ? choice.faculty_profile_id : "";
          return <fieldset key={r.import_key} className="grid gap-2 border-t border-rule pt-3">
            <legend className="font-medium">{name}</legend>
            {r.import_key !== name && <p className="text-muted-foreground">{r.import_key}</p>}
            <label className="grid gap-1">{words.instructorRecordFor} {name}<select value={value} onChange={(e) => choose(r.import_key, e.target.value)}>
              <option value="">{words.chooseRecord}</option>
              <option value="create">{words.createAccountless}</option>
              {(faculty.data ?? []).filter((f) => !f.archived_at || f.id === value).map((f) => <option key={f.id} value={f.id} disabled={Boolean(f.archived_at)}>
                {personChoice(f.display_name, f.email)}{f.archived_at ? words.archivedSuffix : ""}</option>)}
            </select></label>
          </fieldset>;
        })}
      </div> }];
  };
  return useImportTask(BOX, words.closedTermRecordsTask, "historical", (source, setSource) => {
    const read = async (file: File | undefined) => {
      if (!file) return;
      try {
        const bytes = await file.arrayBuffer();
        const digest = await crypto.subtle.digest("SHA-256", bytes);
        const hash = Array.from(new Uint8Array(digest), (v) => v.toString(16).padStart(2, "0")).join("");
        const result = readRecordsSource(file.name, new TextDecoder().decode(bytes));
        kept.set(result.table ? { digest: hash, table: result.table } : null);
        if (!result.table) { setSource({ name: file.name, digest: hash, scope: hash, rows: result.rows, problems: result.problems }); return; }
        const map = prefillColumns(RECORDS_FIELDS, result.table.header);
        const mapped = readMappedRecords(result.table, map);
        setSource({ name: file.name, digest: hash, scope: hash, rows: mapped.rows, problems: mapped.problems, review: map });
      } catch (e) {
        setSource({ name: file.name, digest: "", rows: [], problems: [e instanceof Error ? e.message : words.fileCouldNotBeRead] });
      }
    };
    return <div className="grid gap-3 text-ui">
      <p>{columnsWords.recordsIntro}</p>
      <p className="text-muted-foreground">{words.recordsBounded}</p>
      <SourceFormat shapes={SHAPES} />
      <label className="grid gap-2">{words.recordsFile}<input type="file" accept=".csv,.json" onChange={(e) => void read(e.target.files?.[0])} /></label>
      {source && <p>{text.fileRows(source.name, source.rows.length)}{source.rows.length ? ` · ${text.recordsSummary(summariseRecords(source.rows))}` : ""}</p>}
      {source && columnMapOf(source) && tableOf(source) && <p className="text-muted-foreground">{columnsText.exportLines(tableOf(source)!.records.length)}</p>}
      {source?.problems.map((problem) => <p role="alert" key={problem}>{problem}</p>)}
    </div>;
  }, { sourceLabel: words.recordsFile, emptyDetail: words.chooseFileOrTemplate, sourceParts, sourceBlockers, rank }).box;
}
